import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  Skeleton,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { getMyGotySelections, deleteGotySelection, deleteUserGoty } from '../services/goty_api';
import { UserGotySelection, GotyCategory, GotyCategoryLabels } from '../types/Goty';
import { SITE_NAME } from '../constants/site';
import GotyGameSelectDialog from '../components/dialogs/GotyGameSelectDialog';
import ConfirmDialog from '../components/dialogs/ConfirmDialog';
import Tips from '../components/dialogs/Tips';

const EditGotyPage: React.FC = () => {
  const { year } = useParams<{ year: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [selections, setSelections] = useState<UserGotySelection[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<GotyCategory | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteSelectionDialogOpen, setDeleteSelectionDialogOpen] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState<GotyCategory | null>(null);

  const currentYear = parseInt(year || '2025');
  const isMyGoty = location.pathname.startsWith('/mygoty/');

  useEffect(() => {
    fetchSelections();
  }, [currentYear]);

  const fetchSelections = async () => {
    try {
      setLoading(true);
      const response = await getMyGotySelections(currentYear, isMyGoty);
      setSelections(response.selections);
    } catch (error) {
      console.error('Failed to fetch GOTY selections:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategorySelection = (category: GotyCategory) => {
    return selections.find(s => s.category === category);
  };

  const handleEdit = (category: GotyCategory) => {
    setEditingCategory(category);
    setDialogOpen(true);
  };

  const handleDelete = (category: GotyCategory) => {
    setDeletingCategory(category);
    setDeleteSelectionDialogOpen(true);
  };

  const handleDeleteSelectionConfirm = async () => {
    if (deletingCategory === null) return;

    try {
      await deleteGotySelection(currentYear, deletingCategory, isMyGoty);
      await fetchSelections();
      setDeleteSelectionDialogOpen(false);
      setDeletingCategory(null);
    } catch (error) {
      console.error('Failed to delete selection:', error);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingCategory(null);
  };

  const handleSaveSelection = async () => {
    await fetchSelections();
  };

  const handleDeleteGoty = async () => {
    try {
      await deleteUserGoty(currentYear, isMyGoty);
      navigate('/profile?tab=goty');
    } catch (error) {
      console.error('Failed to delete GOTY:', error);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 4, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Container>
    );
  }

  const pageTitle = `GOTY ${currentYear} を編集 | ${SITE_NAME}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <Box>
        {/* ヘッダー */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
            >
              戻る
            </Button>
            <Button
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              color="error"
              variant="outlined"
            >
              このGOTYを削除
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {isMyGoty ? 'MyGOTY' : 'GOTY'} {currentYear}
            </Typography>
            <Tips
              title="GOTY Categories Guide"
              maxWidth="md"
              buttonComponent={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
                  <HelpOutlineIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.8rem',
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: 'rgba(255, 255, 255, 0.9)'
                      }
                    }}
                  >
                    GOTYガイドを見る
                  </Typography>
                </Box>
              }
              content={
                <Box>
                  <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                    ゲームオブザイヤー
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                    その年で最も優れたゲーム、または最も印象に残ったゲームを選んでください。全ての要素を総合的に評価し、あなたにとって最高のゲーム体験を提供した作品を選びましょう。
                  </Typography>

                  <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                    ベストビジュアル
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                    アートスタイル、グラフィック品質、視覚演出が最も優れていたゲームを選んでください。美しいグラフィックや印象的なビジュアル表現が魅力的な作品が対象です。
                  </Typography>

                  <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                    ベストサウンド
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                    音楽、効果音、音声演技、音響演出が最も優れていたゲームを選んでください。BGMが印象的だった、効果音が心地よかった、ボイスが魅力的だったなど、聴覚体験が素晴らしかった作品が対象です。
                  </Typography>

                  <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                    ベストゲームプレイ
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                    ゲームとして「遊び」の部分が最も優れていたゲームを選んでください。操作していて気持ちいい、システムが面白い、バランスが良いなど、プレイ体験そのものが楽しかった作品が対象です。
                  </Typography>

                  <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                    ベストナラティブ
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                    ストーリー、キャラクター、世界観、演出が最も優れていたゲームを選んでください。物語に引き込まれた、キャラクターに感情移入した、世界観に没入したなど、ナラティブ体験が印象的だった作品が対象です。
                  </Typography>

                  <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                    ベストオリジナリティ
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                    最も独創的で革新的だったゲームを選んでください。斬新なアイデア、ユニークなシステム、新しい体験を提供した作品が対象です。
                  </Typography>

                  <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                    ベストマルチプレイヤー
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                    マルチプレイ要素が最も優れていたゲームを選んでください。協力プレイや対戦が楽しかった、オンラインコミュニティが活発だったなど、マルチプレイ体験が素晴らしかった作品が対象です。
                  </Typography>

                  <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                    ベストインディー
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                    インディーゲームの中で最も優れていた作品を選んでください。小規模スタジオや個人開発者が制作した、創造性豊かで独特な魅力を持つゲームが対象です。
                  </Typography>

                  <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                    ベストリメイク
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                    リメイク・リマスター作品の中で最も優れていた作品を選んでください。原作の魅力を残しつつ、現代的なアップデートが施された作品が対象です。
                  </Typography>

                  <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                    ベストアクセシビリティ
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#dadada' }}>
                    年齢・性別・身体的特徴に関わらず、誰もがゲームを楽しめるように配慮されたゲームを選んでください。字幕や色覚調整、操作カスタマイズ、難易度オプションなど、多様なプレイヤーのニーズに応える工夫が施された作品が対象です。
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                    {`・視覚サポート（字幕、色覚調整、UIサイズ変更）
・聴覚サポート（視覚的な音声表示、音声ガイド）
・操作サポート（ボタン割り当て変更、自動エイム、簡易操作モード）
・その他（難易度調整、エフェクト軽減、一時停止機能）`}
                  </Typography>
                </Box>
              }
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {isMyGoty
              ? 'その年に遊んだお気に入りのゲームを選択してください（発売年制約なし）'
              : '各カテゴリーでお気に入りのゲームを選択してください'}
          </Typography>
        </Box>

        {/* カテゴリー一覧 */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          {Object.values(GotyCategory).filter(v => typeof v === 'number').map((category) => {
            const categoryNum = category as GotyCategory;
            const selection = getCategorySelection(categoryNum);
            const label = GotyCategoryLabels[categoryNum];

            return (
              <Card
                key={categoryNum}
                sx={{
                  p: 3,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {label.ja}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size='small'
                      onClick={() => handleEdit(categoryNum)}
                    >
                      {selection ? '変更' : '選択'}
                    </Button>
                    {selection && (
                      <Button
                        variant="outlined"
                        size='small'
                        color="secondary"
                        onClick={() => handleDelete(categoryNum)}
                      >
                        削除
                      </Button>
                    )}
                  </Box>
                </Box>

                {selection?.game ? (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {selection.game.cover && (
                      <Box
                        component="img"
                        src={selection.game.cover}
                        alt={selection.game.name}
                        sx={{ width: 80, height: 107, objectFit: 'cover', borderRadius: 1 }}
                      />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {selection.game.japanese_name || selection.game.name}
                      </Typography>
                      {selection.comment && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          "{selection.comment}"
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    未選択
                  </Typography>
                )}
              </Card>
            );
          })}
        </Box>
      </Box>

      {/* ゲーム選択ダイアログ */}
      {editingCategory !== null && (
        <GotyGameSelectDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          onSave={handleSaveSelection}
          year={currentYear}
          category={editingCategory}
          currentGame={getCategorySelection(editingCategory)?.game}
          currentComment={getCategorySelection(editingCategory)?.comment}
          isMyGoty={isMyGoty}
        />
      )}

      {/* GOTY全体削除確認ダイアログ */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="GOTYを削除"
        message={`${isMyGoty ? 'MyGOTY' : 'GOTY'} ${currentYear} を削除してもよろしいですか？この操作は取り消せません。`}
        onConfirm={handleDeleteGoty}
        onCancel={() => setDeleteDialogOpen(false)}
        confirmText="削除"
        cancelText="キャンセル"
      />

      {/* ゲーム選択削除確認ダイアログ */}
      <ConfirmDialog
        open={deleteSelectionDialogOpen}
        title="選択を削除"
        message="この選択を削除してもよろしいですか？"
        onConfirm={handleDeleteSelectionConfirm}
        onCancel={() => {
          setDeleteSelectionDialogOpen(false);
          setDeletingCategory(null);
        }}
        confirmText="削除"
        cancelText="キャンセル"
      />
    </>
  );
};

export default EditGotyPage;
